/**
 * Create-access-method dialog.
 *
 * The kind selector reveals only the fields that kind needs. Composition
 * (picking a transport) is a separate, optional row at the bottom — the
 * dialog filters the transport picker to peers in the same org and hides
 * any choice that would be a kind-composability error on the server.
 *
 * Credentials wiring is intentionally a TODO note: secrets management lands
 * in a follow-up round (currently the secrets table exists but there's no
 * UI to create one), so for now we just show the credentialsRef field as a
 * raw text input for operators who already have a secret id.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Loader2, Terminal, Container, Layers as LayersIcon, Cpu } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { SecretPicker } from '@/components/applications/SecretPicker.js';
import {
  api,
  ApiError,
  type AccessMethod,
  type AccessMethodKind,
  type PrincipalResponse,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';

type Membership = PrincipalResponse['memberships'][number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberships: Membership[];
  /** Existing methods in the chosen org — used to populate the transport picker. */
  reachableMethodsByOrg: Record<string, AccessMethod[]>;
  onCreated: (created: AccessMethod) => void | Promise<void>;
}

const KIND_OPTIONS: Array<{
  value: AccessMethodKind;
  label: string;
  hint: string;
  icon: typeof Terminal;
}> = [
  { value: 'ssh', label: 'SSH', hint: 'Direct shell over the network. Most common leaf.', icon: Terminal },
  {
    value: 'docker',
    label: 'Docker',
    hint: 'docker exec inside a container, usually wrapping an SSH transport.',
    icon: Container,
  },
  {
    value: 'kubectl',
    label: 'kubectl',
    hint: 'kubectl exec into a pod, usually wrapping an SSH bastion.',
    icon: LayersIcon,
  },
  {
    value: 'local',
    label: 'Local',
    hint: 'Runs on the DeployIt host directly. No transport allowed.',
    icon: Cpu,
  },
];

/** What kinds may be wrapped by `outer` — mirror of the server-side allow-list. */
function kindsValidUnder(outer: AccessMethodKind): AccessMethodKind[] {
  if (outer === 'local') return [];
  if (outer === 'ssh') return ['local', 'ssh'];
  // docker, kubectl, ... anything except local-only above
  return ['ssh', 'docker', 'kubectl', 'local'];
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

export function CreateAccessMethodDialog({
  open,
  onOpenChange,
  memberships,
  reachableMethodsByOrg,
  onCreated,
}: Props): JSX.Element {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useState(memberships[0]?.orgId ?? '');
  const [kind, setKind] = useState<AccessMethodKind>('ssh');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [transportId, setTransportId] = useState<string>('');
  const [credentialsRef, setCredentialsRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // kind-specific fields
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [sshUser, setSshUser] = useState('');
  const [sshFingerprint, setSshFingerprint] = useState('');
  const [dockerContainer, setDockerContainer] = useState('');
  const [dockerUser, setDockerUser] = useState('');
  const [kubectlContext, setKubectlContext] = useState('');
  const [kubectlNamespace, setKubectlNamespace] = useState('');
  const [kubectlPod, setKubectlPod] = useState('');
  const [kubectlContainer, setKubectlContainer] = useState('');

  const reachable = reachableMethodsByOrg[orgId] ?? [];
  const allowedTransports = useMemo(() => {
    const allowedKinds = new Set(kindsValidUnder(kind));
    return reachable.filter((r) => allowedKinds.has(r.kind));
  }, [kind, reachable]);

  function reset() {
    setOrgId(memberships[0]?.orgId ?? '');
    setKind('ssh');
    setName('');
    setDescription('');
    setTransportId('');
    setCredentialsRef('');
    setSshHost('');
    setSshPort('22');
    setSshUser('');
    setSshFingerprint('');
    setDockerContainer('');
    setDockerUser('');
    setKubectlContext('');
    setKubectlNamespace('');
    setKubectlPod('');
    setKubectlContainer('');
    setError(null);
    setSubmitting(false);
  }

  function buildSettings(): Record<string, unknown> {
    switch (kind) {
      case 'ssh':
        return {
          host: sshHost,
          user: sshUser,
          ...(sshPort && sshPort !== '22' ? { port: Number(sshPort) } : {}),
          ...(sshFingerprint ? { knownHostsFingerprint: sshFingerprint } : {}),
        };
      case 'docker':
        return {
          container: dockerContainer,
          ...(dockerUser ? { user: dockerUser } : {}),
        };
      case 'kubectl':
        return {
          context: kubectlContext,
          ...(kubectlNamespace ? { namespace: kubectlNamespace } : {}),
          ...(kubectlPod ? { pod: kubectlPod } : {}),
          ...(kubectlContainer ? { container: kubectlContainer } : {}),
        };
      case 'local':
        return {};
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!orgId) {
      setError('Pick an organisation.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.accessMethods.create(orgId, {
        name,
        kind,
        settings: buildSettings(),
        ...(transportId ? { transportAccessMethodId: transportId } : {}),
        ...(credentialsRef ? { credentialsRef } : {}),
        ...(description ? { description } : {}),
      });
      toast.success(`Created ${created.name}`);
      await onCreated(created);
      onOpenChange(false);
      reset();
      await navigate({
        to: '/p/applications/access-methods/$id',
        params: { id: created.id },
        search: { org: created.orgSlug },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create access method.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New access method</DialogTitle>
          <DialogDescription>
            How DeployIt reaches a target. Pick a kind, supply its settings, optionally wrap an
            existing access method as the transport.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="org">Organisation</Label>
            <select
              id="org"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setTransportId(''); // reset transport choice when org changes
              }}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {memberships.map((m) => (
                <option key={m.orgId} value={m.orgId}>
                  {m.orgName} (/{m.orgSlug})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label>Kind</Label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {KIND_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = kind === opt.value;
                return (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => {
                      setKind(opt.value);
                      if (opt.value === 'local') setTransportId('');
                    }}
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-md border px-2.5 py-2 text-left transition-colors',
                      selected ? 'border-primary bg-primary/10' : 'border-border bg-background hover:bg-accent/40',
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="h-3.5 w-3.5" />
                      {opt.label}
                    </span>
                    <span className="text-[11px] leading-snug text-muted-foreground">{opt.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={kind === 'ssh' ? 'web-01 SSH' : kind === 'docker' ? 'nginx via SSH' : 'prod cluster'}
            />
            {name && (
              <p className="text-xs text-muted-foreground">
                Slug: <span className="font-mono">{slugify(name)}</span>
              </p>
            )}
          </div>

          {kind === 'ssh' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="host">Host</Label>
                <Input
                  id="host"
                  required
                  value={sshHost}
                  onChange={(e) => setSshHost(e.target.value)}
                  placeholder="web-01.example.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="user">User</Label>
                <Input
                  id="user"
                  required
                  value={sshUser}
                  onChange={(e) => setSshUser(e.target.value)}
                  placeholder="deploy"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="port">Port</Label>
                <Input
                  id="port"
                  type="number"
                  value={sshPort}
                  onChange={(e) => setSshPort(e.target.value)}
                  placeholder="22"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fp">Host key fingerprint</Label>
                <Input
                  id="fp"
                  value={sshFingerprint}
                  onChange={(e) => setSshFingerprint(e.target.value)}
                  placeholder="SHA256:… (optional, recommended)"
                  className="font-mono text-xs"
                />
              </div>
            </div>
          )}

          {kind === 'docker' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="container">Container</Label>
                <Input
                  id="container"
                  required
                  value={dockerContainer}
                  onChange={(e) => setDockerContainer(e.target.value)}
                  placeholder="nginx"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="duser">User inside container</Label>
                <Input
                  id="duser"
                  value={dockerUser}
                  onChange={(e) => setDockerUser(e.target.value)}
                  placeholder="(optional)"
                />
              </div>
            </div>
          )}

          {kind === 'kubectl' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ctx">Context</Label>
                <Input
                  id="ctx"
                  required
                  value={kubectlContext}
                  onChange={(e) => setKubectlContext(e.target.value)}
                  placeholder="prod-cluster"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ns">Namespace</Label>
                <Input
                  id="ns"
                  value={kubectlNamespace}
                  onChange={(e) => setKubectlNamespace(e.target.value)}
                  placeholder="default"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pod">Pod</Label>
                <Input
                  id="pod"
                  value={kubectlPod}
                  onChange={(e) => setKubectlPod(e.target.value)}
                  placeholder="(optional)"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cct">Container</Label>
                <Input
                  id="cct"
                  value={kubectlContainer}
                  onChange={(e) => setKubectlContainer(e.target.value)}
                  placeholder="(optional)"
                />
              </div>
            </div>
          )}

          {kind !== 'local' && (
            <div className="space-y-1.5">
              <Label htmlFor="transport">Transport (optional)</Label>
              <select
                id="transport"
                value={transportId}
                onChange={(e) => setTransportId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">— none (direct leaf) —</option>
                {allowedTransports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.kind})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                For Docker / kubectl, pick an SSH leaf as the transport. SSH itself is normally a
                bare leaf with no transport.
              </p>
            </div>
          )}

          {kind !== 'local' && (
            <div className="space-y-1.5">
              <Label htmlFor="cred">Credential</Label>
              <SecretPicker orgId={orgId} value={credentialsRef} onChange={setCredentialsRef} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="desc">Description</Label>
            <Input
              id="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="(optional)"
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name || !orgId}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create access method'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
